import { FulfillmentTypeListResponseType } from "@sellar-io/api-v2/types";
import { formatCurrency, pluralize } from "@sellar-io/common";
import { ReactNode } from "react";
import { useBasketContext } from "../../hooks";
import { useSupplierStorefrontV2 } from "../../hooks/api/v2/supplier/useSupplierStorefrontV2";
import { useStockListOrderSummaryProps } from "../../pages";

import { DeliveryChargeCapToolTip } from "../deliveryChargeToolTip/DeliveryChargeCapToolTip";
import { Icon, H5 } from "@sellar-io/components";
import { FulfillmentTypeStartingPrice } from "./FulfillmentTypeStartingPrice";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";

export const DeliveryOptionsCardCompact = ({
  title,
  description,
  fulfillmentType,
  supplierId,
  children,
}: {
  title: ReactNode;
  supplierId: number;
  description?: ReactNode;
  fulfillmentType:
    | Omit<
        FulfillmentTypeListResponseType[number],
        | "id"
        | "name"
        | "OrderRules"
        | "numberOfCustomers"
        | "numberOfZones"
        | "DeliveryMethodContainerRules"
      >
    | undefined;
  children?: ReactNode;
}) => {
  return (
    <div className={"flex flex-col gap-4"}>
      <div className="flex flex-col">
        {title} {description}
      </div>
      <Pricing supplierId={supplierId} fulfillmentType={fulfillmentType} />
      {children}
    </div>
  );
};

const Pricing = ({
  supplierId,
  fulfillmentType,
}: {
  supplierId: number;
  fulfillmentType:
    | Omit<
        FulfillmentTypeListResponseType[number],
        | "id"
        | "name"
        | "OrderRules"
        | "numberOfCustomers"
        | "numberOfZones"
        | "DeliveryMethodContainerRules"
      >
    | undefined;
}) => {
  const supplierStorefrontQuery = useSupplierStorefrontV2(supplierId, {});
  const [{ order }] = useBasketContext();
  const supplierStorefront = supplierStorefrontQuery.data;

  const priceTierDiscounts =
    supplierStorefront?.VolumeDiscounts?.PriceTierDiscounts ?? [];

  const palletOverrideEnabled = useRemoteFeatureFlags().isActive(
    "courierHaulierEnforcement",
  );

  const priceTierOrderRules =
    supplierStorefront?.FulfillmentType.OrderRules?.PriceTierOrderRules ??
    supplierStorefront?.OrderRules?.PriceTierOrderRules ??
    [];

  const minimumOrderPrice =
    supplierStorefront?.FulfillmentType.OrderRules?.minimumOrderPrice ??
    supplierStorefront?.OrderRules?.minimumOrderPrice ??
    0;

  const freeDeliveryBasketSubtotal =
    supplierStorefront?.FulfillmentType.freeDeliveryBasketSubtotal ?? null;

  const {
    deliveryChargeWithoutRules,
    apiValues: { deliveryCharge, isDeliveryChargeCapped },
  } = useStockListOrderSummaryProps({
    order: order,
    deliveryChargeRules: fulfillmentType?.DeliveryChargeRules,
    deliveryCharge: fulfillmentType?.charge,
    deliveryChargeCap: fulfillmentType?.deliveryChargeCap,
    fulfillmentTypeContainerDeliveryCharges:
      fulfillmentType?.FulfillmentTypeContainerDeliveryCharges,
    priceTierDiscounts,
    containerTypeDeliveryChargeRules:
      fulfillmentType?.ContainerTypeDeliveryChargeRules ?? [],
    caseSizeRuleList: supplierStorefront?.CaseSizeRules.list ?? [],
    priceTierOrderRules,
    minimumOrderPrice,
    freeDeliveryBasketSubtotal,
  });

  if (deliveryCharge > 0) {
    if (isDeliveryChargeCapped) {
      return (
        <DeliveryChargeCapToolTip>
          <div className="flex flex-col">
            <div
              className={
                "inline-flex cursor-pointer items-center gap-1 font-bold"
              }
            >
              <Icon icon={"INFORMATION_OUTLINE"} size={0.65} />
              {formatCurrency(deliveryCharge)}
            </div>
            <small className={"text-zinc-500"}>
              Maximum courier charge reached. Delivery may be via pallet.
            </small>
          </div>
        </DeliveryChargeCapToolTip>
      );
    }

    if (fulfillmentType?.fulfillmentType === "haulier") {
      const numberOfItems =
        order?.OrderItems?.reduce((num, item) => {
          return num + item.quantity;
        }, 0) ?? 1;

      return (
        <div className="flex flex-col gap-2">
          <H5>{formatCurrency(deliveryCharge)} (ex. VAT)</H5>
          {numberOfItems > 0 && palletOverrideEnabled && (
            <H5>{`${numberOfItems} ${pluralize("item", numberOfItems)} • ${formatCurrency(deliveryCharge / numberOfItems)} each`}</H5>
          )}
        </div>
      );
    }

    return <H5>{formatCurrency(deliveryCharge)} (ex. VAT)</H5>;
  }

  if (deliveryChargeWithoutRules > 0) {
    return (
      <div className={"inline-flex items-center gap-1"}>
        <span className={"text-sm text-zinc-500 line-through"}>
          {formatCurrency(deliveryChargeWithoutRules)}
        </span>
        <H5>FREE</H5>
      </div>
    );
  }

  return (
    <FulfillmentTypeStartingPrice
      fulfillmentTypeCharge={fulfillmentType?.charge}
      orderItemLength={order?.OrderItems?.length ?? 0}
      fulfillmentType={fulfillmentType?.fulfillmentType}
      hasPerItemCharges={
        fulfillmentType?.FulfillmentTypeContainerDeliveryCharges.some(
          (containerCharge) => containerCharge.charge > 0,
        ) ?? false
      }
    />
  );
};
